/**
 * AccountPage.js - 用户个人中心页面 - 中式典雅风格
 * 配色：宣纸白 (#F8F6F1)、茶褐 (#7D6B55)、竹青 (#6B8E7C)、朱砂红 (#C44536)
 * 功能：查看/编辑个人信息、修改密码
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Card,
  Typography,
  message,
  Tabs,
  Divider,
  Space,
  Tag,
  Modal,
  Row,
  Col,
  Spin,
  Checkbox,
} from 'antd';
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  LockOutlined,
  SafetyOutlined,
  KeyOutlined,
  EnvironmentOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import StorefrontLayout from '../../components/storefront/StorefrontLayout';
import { useAuth } from '../../contexts/AuthContext';
import { customerApi } from '../../api/index';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

// 中式配色
const COLORS = {
  paper: '#F8F6F1',
  teaBrown: '#7D6B55',
  bambooGreen: '#6B8E7C',
  cinnabar: '#C44536',
  ink: '#2C2C2C',
  lightInk: '#5A5A5A',
  lightBorder: '#E0D8D0',
};

export default function AccountPage() {
  const navigate = useNavigate();
  const { isLoggedIn, user } = useAuth();

  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [addressForm] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [pwdLoading, setPwdLoading] = useState(false);
  const [profile, setProfile] = useState(null);

  // 地址管理相关状态
  const [addresses, setAddresses] = useState([]);
  const [addrLoading, setAddrLoading] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);

  // 未登录跳转
  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login');
    }
  }, [isLoggedIn, navigate]);

  // 加载用户信息
  useEffect(() => {
    if (isLoggedIn) {
      fetchProfile();
      fetchAddresses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const data = await customerApi.getProfile();
      setProfile(data);
      form.setFieldsValue({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        // 是否订阅营销邮件（未设置时默认订阅）
        marketingEmailSubscribed: data.marketingEmailSubscribed !== false,
      });
    } catch (err) {
      message.error('加载用户信息失败');
    } finally {
      setLoading(false);
    }
  };

  /** 获取地址列表 */
  const fetchAddresses = async () => {
    setAddrLoading(true);
    try {
      const data = await customerApi.getAddresses();
      setAddresses(data || []);
    } catch (err) {
      message.error('加载地址列表失败');
    } finally {
      setAddrLoading(false);
    }
  };

  /** 打开新增地址模态框 */
  const showAddAddress = () => {
    setEditingAddress(null);
    addressForm.resetFields();
    setIsModalVisible(true);
  };

  /** 打开编辑地址模态框 */
  const showEditAddress = (addr) => {
    setEditingAddress(addr);
    addressForm.setFieldsValue({
      firstName: addr.firstName,
      lastName: addr.lastName,
      address1: addr.addressLine1,
      address2: addr.addressLine2,
      city: addr.city,
      province: addr.province,
      phone: addr.phone,
      isDefault: addr.isDefault,
    });
    setIsModalVisible(true);
  };

  /** 删除地址 */
  const handleDeleteAddress = (id) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除该收货地址吗？',
      okText: '删除',
      cancelText: '取消',
      danger: true,
      onOk: async () => {
        try {
          await customerApi.deleteAddress(id);
          message.success('地址已删除');
          fetchAddresses();
        } catch (err) {
          message.error('删除失败');
        }
      },
    });
  };

  /** 保存地址（新增或编辑） */
  const handleSaveAddress = async (values) => {
    // 转换字段名以匹配后端 DTO
    const addressData = {
      firstName: values.firstName,
      lastName: values.lastName,
      addressLine1: values.address1,
      addressLine2: values.address2,
      city: values.city,
      province: values.province,
      phone: values.phone,
      isDefault: values.isDefault,
    };
    try {
      if (editingAddress) {
        await customerApi.updateAddress(editingAddress.id, addressData);
        message.success('地址已更新');
      } else {
        await customerApi.addAddress(addressData);
        message.success('地址已添加');
      }
      setIsModalVisible(false);
      fetchAddresses();
    } catch (err) {
      message.error(err?.response?.data?.message || '保存失败');
    }
  };

  /** 更新个人信息 */
  const handleUpdateProfile = async (values) => {
    setLoading(true);
    try {
      await customerApi.updateProfile(values);
      message.success('个人信息已更新');
      fetchProfile();
    } catch (err) {
      message.error(err?.response?.data?.message || '更新失败');
    } finally {
      setLoading(false);
    }
  };

  /** 修改密码 */
  const handleChangePassword = async (values) => {
    if (values.newPassword !== values.confirmPassword) {
      message.error('两次输入的新密码不一致');
      return;
    }

    setPwdLoading(true);
    try {
      await customerApi.changePassword({
        oldPassword: values.oldPassword,
        newPassword: values.newPassword,
      });
      message.success('密码修改成功');
      passwordForm.resetFields();
    } catch (err) {
      message.error(err?.response?.data?.message || '修改失败');
    } finally {
      setPwdLoading(false);
    }
  };

  return (
    <StorefrontLayout>
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '32px 24px 64px' }}>
        <Title
          level={2}
          style={{
            marginBottom: 32,
            color: COLORS.ink,
            fontSize: 24,
            letterSpacing: '0.15em',
          }}
        >
          账户设置
        </Title>

        <Card
          style={{
            borderRadius: 12,
            border: 'none',
            boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
          }}
        >
          <Tabs
            defaultActiveKey="profile"
            tabBarStyle={{ borderBottom: `1px solid ${COLORS.lightBorder}` }}
          >
            {/* 个人信息标签页 */}
            <TabPane
              tab={
                <span>
                  <UserOutlined />
                  个人信息
                </span>
              }
              key="profile"
            >
              <Form
                form={form}
                layout="vertical"
                onFinish={handleUpdateProfile}
                size="large"
                initialValues={{ email: profile?.email }}
              >
                <Form.Item
                  name="email"
                  label={<span style={{ color: COLORS.ink, fontWeight: 500 }}>邮箱</span>}
                  rules={[
                    { required: true, message: '请输入邮箱' },
                    { type: 'email', message: '请输入有效的邮箱地址' },
                  ]}
                >
                  <Input
                    prefix={<MailOutlined style={{ color: COLORS.bambooGreen }} />}
                    disabled
                    style={{ borderRadius: 8, background: `${COLORS.teaBrown}08` }}
                  />
                </Form.Item>

                <Divider style={{ margin: '16px 0', borderColor: COLORS.lightBorder }} />

                <Form.Item
                  name="firstName"
                  label={<span style={{ color: COLORS.ink, fontWeight: 500 }}>名字</span>}
                  rules={[{ required: true, message: '请输入名字' }]}
                >
                  <Input
                    prefix={<UserOutlined style={{ color: COLORS.bambooGreen }} />}
                    placeholder="名字"
                    style={{ borderRadius: 8 }}
                  />
                </Form.Item>

                <Form.Item
                  name="lastName"
                  label={<span style={{ color: COLORS.ink, fontWeight: 500 }}>姓氏</span>}
                  rules={[{ required: true, message: '请输入姓氏' }]}
                >
                  <Input
                    prefix={<UserOutlined style={{ color: COLORS.bambooGreen }} />}
                    placeholder="姓氏"
                    style={{ borderRadius: 8 }}
                  />
                </Form.Item>

                <Form.Item
                  name="phone"
                  label={<span style={{ color: COLORS.ink, fontWeight: 500 }}>手机号</span>}
                >
                  <Input
                    prefix={<PhoneOutlined style={{ color: COLORS.bambooGreen }} />}
                    placeholder="手机号"
                    style={{ borderRadius: 8 }}
                  />
                </Form.Item>

                <Divider style={{ margin: '16px 0', borderColor: COLORS.lightBorder }} />

                {/* 营销邮件订阅开关：取消勾选后将不再收到促销活动邮件 */}
                <Form.Item name="marketingEmailSubscribed" valuePropName="checked" style={{ marginBottom: 0 }}>
                  <Checkbox>
                    <span style={{ color: COLORS.ink }}>订阅营销邮件</span>
                    <span style={{ color: COLORS.lightInk, marginLeft: 8, fontSize: 13 }}>
                      （接收优惠活动、上新等通知）
                    </span>
                  </Checkbox>
                </Form.Item>

                <Form.Item style={{ marginTop: 24 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    block
                    size="large"
                    style={{
                      borderRadius: 8,
                      background: COLORS.teaBrown,
                      borderColor: COLORS.teaBrown,
                      height: 48,
                    }}
                  >
                    保存修改
                  </Button>
                </Form.Item>
              </Form>
            </TabPane>

            {/* 修改密码标签页 */}
            <TabPane
              tab={
                <span>
                  <LockOutlined />
                  修改密码
                </span>
              }
              key="password"
            >
              <div style={{ marginBottom: 16 }}>
                <Text style={{ color: COLORS.lightInk }}>
                  密码至少 6 位，请定期更换密码以保障账户安全
                </Text>
              </div>

              <Form
                form={passwordForm}
                layout="vertical"
                onFinish={handleChangePassword}
                size="large"
              >
                <Form.Item
                  name="oldPassword"
                  label={<span style={{ color: COLORS.ink, fontWeight: 500 }}>当前密码</span>}
                  rules={[{ required: true, message: '请输入当前密码' }]}
                >
                  <Input.Password
                    prefix={<LockOutlined style={{ color: COLORS.bambooGreen }} />}
                    placeholder="请输入当前密码"
                    autoComplete="current-password"
                    style={{ borderRadius: 8 }}
                  />
                </Form.Item>

                <Divider style={{ margin: '16px 0', borderColor: COLORS.lightBorder }} />

                <Form.Item
                  name="newPassword"
                  label={<span style={{ color: COLORS.ink, fontWeight: 500 }}>新密码</span>}
                  rules={[
                    { required: true, message: '请输入新密码' },
                    { min: 6, message: '密码至少 6 位' },
                  ]}
                >
                  <Input.Password
                    prefix={<SafetyOutlined style={{ color: COLORS.bambooGreen }} />}
                    placeholder="请输入新密码"
                    autoComplete="new-password"
                    style={{ borderRadius: 8 }}
                  />
                </Form.Item>

                <Form.Item
                  name="confirmPassword"
                  label={<span style={{ color: COLORS.ink, fontWeight: 500 }}>确认新密码</span>}
                  rules={[
                    { required: true, message: '请确认新密码' },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('newPassword') === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('两次输入的密码不一致'));
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    prefix={<KeyOutlined style={{ color: COLORS.bambooGreen }} />}
                    placeholder="请再次输入新密码"
                    autoComplete="new-password"
                    style={{ borderRadius: 8 }}
                  />
                </Form.Item>

                <Form.Item style={{ marginTop: 24 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={pwdLoading}
                    block
                    size="large"
                    danger
                    style={{
                      borderRadius: 8,
                      background: COLORS.cinnabar,
                      borderColor: COLORS.cinnabar,
                      height: 48,
                    }}
                  >
                    修改密码
                  </Button>
                </Form.Item>
              </Form>
            </TabPane>

            {/* 地址管理标签页 */}
            <TabPane
              tab={
                <span>
                  <EnvironmentOutlined />
                  收货地址
                </span>
              }
              key="addresses"
            >
              <div style={{ marginBottom: 16 }}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={showAddAddress}
                  style={{
                    borderRadius: 8,
                    background: COLORS.teaBrown,
                    borderColor: COLORS.teaBrown,
                  }}
                >
                  新增地址
                </Button>
              </div>

              {addrLoading ? (
                <div style={{ textAlign: 'center', padding: 32 }}>
                  <Spin />
                </div>
              ) : addresses.length === 0 ? (
                <div
                  style={{
                    textAlign: 'center',
                    padding: 32,
                    color: COLORS.lightInk,
                  }}
                >
                  <EnvironmentOutlined
                    style={{
                      fontSize: 48,
                      marginBottom: 16,
                      color: `${COLORS.teaBrown}30`,
                    }}
                  />
                  <div>暂无收货地址</div>
                </div>
              ) : (
                <Row gutter={[16, 16]}>
                  {addresses.map((addr) => (
                    <Col xs={24} sm={12} key={addr.id}>
                      <Card
                        size="small"
                        title={
                          <Space>
                            <span style={{ color: COLORS.ink }}>{addr.lastName} {addr.firstName}</span>
                            {addr.isDefault && (
                              <Tag
                                style={{
                                  background: `${COLORS.bambooGreen}15`,
                                  borderColor: COLORS.bambooGreen,
                                  color: COLORS.bambooGreen,
                                }}
                              >
                                默认
                              </Tag>
                            )}
                          </Space>
                        }
                        extra={
                          <Space size="small">
                            <Button
                              type="link"
                              size="small"
                              icon={<EditOutlined style={{ color: COLORS.teaBrown }} />}
                              onClick={() => showEditAddress(addr)}
                            />
                            <Button
                              type="link"
                              size="small"
                              danger
                              icon={<DeleteOutlined style={{ color: COLORS.cinnabar }} />}
                              onClick={() => handleDeleteAddress(addr.id)}
                            />
                          </Space>
                        }
                        style={{
                          borderRadius: 10,
                          borderColor: COLORS.lightBorder,
                        }}
                      >
                        <div style={{ fontSize: 13, color: COLORS.lightInk, lineHeight: 1.8 }}>
                          <div>电话：{addr.phone}</div>
                          <div>{addr.addressLine1}{addr.addressLine2 ? `, ${addr.addressLine2}` : ''}</div>
                          <div>{[addr.province, addr.city].filter(Boolean).join(', ')}</div>
                        </div>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}

              {/* 地址表单模态框 */}
              <Modal
                title={editingAddress ? '编辑地址' : '新增地址'}
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                onOk={() => addressForm.submit()}
                destroyOnClose
              >
                <Form
                  form={addressForm}
                  layout="vertical"
                  onFinish={handleSaveAddress}
                >
                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item name="lastName" label="姓氏" rules={[{ required: true, message: '请输入姓氏' }]}>
                        <Input placeholder="姓氏" style={{ borderRadius: 8 }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item name="firstName" label="名字" rules={[{ required: true, message: '请输入名字' }]}>
                        <Input placeholder="名字" style={{ borderRadius: 8 }} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item name="phone" label="电话" rules={[{ required: true, message: '请输入电话' }]}>
                    <Input placeholder="联系电话" style={{ borderRadius: 8 }} />
                  </Form.Item>
                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item name="province" label="省份">
                        <Input placeholder="省份" style={{ borderRadius: 8 }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item name="city" label="城市" rules={[{ required: true, message: '请输入城市' }]}>
                        <Input placeholder="城市" style={{ borderRadius: 8 }} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item name="address1" label="街道地址" rules={[{ required: true, message: '请输入街道地址' }]}>
                    <Input placeholder="街道、门牌号" style={{ borderRadius: 8 }} />
                  </Form.Item>
                  <Form.Item name="address2" label="详细地址">
                    <Input placeholder="公寓、楼层等（选填）" style={{ borderRadius: 8 }} />
                  </Form.Item>
                  <Form.Item name="isDefault" label=" " valuePropName="checked">
                    <Checkbox>设为默认地址</Checkbox>
                  </Form.Item>
                </Form>
              </Modal>
            </TabPane>
          </Tabs>
        </Card>

        {/* 返回我的订单链接 */}
        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <Button
            onClick={() => navigate('/orders')}
            style={{
              borderRadius: 8,
              borderColor: COLORS.teaBrown,
              color: COLORS.teaBrown,
            }}
          >
            返回我的订单
          </Button>
        </div>
      </div>
    </StorefrontLayout>
  );
}
